<template>
    <div v-if="items && items.length > 0" :class="['pb-4 lg:pb-0', { 'lg:mb-6': isOpen }]">
        <button @click="isOpen = !isOpen" class="plp-filter">
            <span>{{ label }}</span>
            <IconChevronDown
                :class="{ '!-rotate-180': isOpen }"
                class="size-3 -rotate-90 transition-all lg:-rotate-0" />
        </button>
        <div
            class="overflow-hidden pt-1.5 lg:px-4 lg:duration-300"
            :class="{ 'filter-open': isOpen, 'filter-closed': !isOpen }"
            :aria-hidden="!isOpen"
            role="group">
            <div class="flex h-full flex-col">
                <div class="flex-1 overflow-y-auto">
                    <div class="mt-3 lg:hidden">
                        <button type="button" class="mb-8 flex items-center leading-none" @click="isOpen = false">
                            <ArrowLeftIcon class="mr-3 size-5 stroke-2" />
                            Terug
                        </button>
                        <span class="styled-as-h3 mb-5 block text-xl">{{ label }}</span>
                    </div>
                    <div class="mb-10 grid grid-cols-1 gap-x-16 lg:mb-0">
                        <div class="relative mb-5 lg:px-0">
                            <MagnifyingGlassIcon class="absolute top-1/2 left-4 size-5 -translate-y-[65%] stroke-2" />
                            <input
                                v-model="brandSearch"
                                placeholder="Zoek op merk"
                                class="input mb-1.5 w-full bg-green-300 py-[20px] pr-[28px] !pl-[65px] leading-none placeholder:text-black" />
                        </div>
                        <template v-if="filteredEntities.length === 0">
                            <span class="mb-4 text-sm">Geen resultaten</span>
                        </template>
                        <template v-for="(option, index) in filteredEntities" :key="option.id">
                            <div
                                class="mb-7 flex cursor-pointer items-center text-sm font-normal last:mb-0"
                                v-show="index < 5 || showMoreOptions">
                                <input
                                    type="checkbox"
                                    @change="invokeFilter(option.id, $event)"
                                    :id="option.id"
                                    class="checkbox relative mr-1.5 size-4 cursor-pointer appearance-none rounded border-2 border-black before:absolute before:h-full before:w-full before:bg-green-200 before:bg-[length:12px_12px] before:bg-center before:bg-no-repeat before:duration-200 before:content-[''] checked:before:bg-black checked:before:bg-[url('/check-solid.svg')] focus:outline-none"
                                    :class="{ 'cursor-not-allowed opacity-50': isDefaultBrandOption(option) }"
                                    :checked="isOptionSelected(option.id) || isDefaultBrandOption(option)"
                                    :disabled="
                                        isDefaultBrandOption(option) ||
                                        (keepFilters && !isOptionSelected(option.id) &&
                                            option.customFields?.nd_tweakwise_nrofresults === 0)
                                    " />
                                <label
                                    class="mb-0! flex flex-1 justify-between text-sm leading-0 font-normal"
                                    :class="{ 'cursor-not-allowed opacity-50': isDefaultBrandOption(option) }"
                                    :for="option.id">
                                    <span>
                                        {{ option.translated.name }}
                                        <span v-if="isDefaultBrandOption(option)" class="text-xs font-normal opacity-60"
                                            >(standaard)</span
                                        >
                                    </span>
                                    <span
                                        v-if="option.customFields?.nd_tweakwise_nrofresults"
                                        class="text-sm leading-0 font-normal"
                                        >({{ option.customFields.nd_tweakwise_nrofresults }})</span
                                    >
                                </label>
                            </div>
                        </template>
                        <button
                            v-if="filteredEntities.length > 5"
                            @click="showMoreOptions = !showMoreOptions"
                            class="btn btn-underline !w-max !p-0 text-sm">
                            {{ showMoreOptions ? 'Minder tonen' : 'Meer tonen' }}
                        </button>
                    </div>
                </div>
                <div class="sticky right-0 bottom-0 left-0 bg-white pb-5 lg:hidden">
                    <button
                        type="button"
                        class="btn w-full bg-black text-white"
                        @click="
                            isOpen = false;
                            mobileFiltersMenuStore.closeMenu();
                        ">
                        {{ $snippets['headless.plp.view'] }} {{ total }} {{ $snippets['headless.plp.items'] }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    const keepFilters = useKeepFilters();
    import { MagnifyingGlassIcon, ArrowLeftIcon } from '@heroicons/vue/24/outline';
    import { parsePropertyIds } from '~/utils/plp';

    const props = withDefaults(
        defineProps<{
            label: string;
            code: string;
            index: number;
            entities: Array<any> | undefined;
            options: Array<any> | undefined;
            currentFilters: any;
            total?: number;
        }>(),
        {
            entities: () => [],
            options: () => [],
        },
    );
    const isOpen = defineModel<boolean>('isOpen', { default: true });
    const mobileFiltersMenuStore = useMobileFiltersMenuStore();

    // Use entities if available, otherwise fall back to options
    const items = computed(() => {
        if (props.entities && props.entities.length > 0) return props.entities;
        if (props.options && props.options.length > 0) return props.options;
        return [];
    });
    const showMoreOptions = ref(false);
    const brandSearch = ref('');
    const manufacturerId = inject<string | undefined>('manufacturerId', undefined);

    const route = useRoute();
    const selectedOptions = computed(() => parsePropertyIds(route.query.properties));

    // Detect the default brand option on brand pages:
    // The backend sets nd_tweakwise_isselected=true on the brand's facet option.
    // The default brand is the one that is "isselected" by Tweakwise but NOT explicitly in the URL.
    const isDefaultBrandOption = (option: any): boolean => {
        if (!manufacturerId) return false;
        return option.customFields?.nd_tweakwise_isselected === true && !selectedOptions.value?.includes(option.id);
    };

    const sortedEntities = computed(() => {
        if (!items.value || items.value.length === 0) return [];

        // When expanded ("Meer tonen"), sort by alternatesortorder
        if (showMoreOptions.value) {
            return [...items.value].sort((a, b) => {
                const orderA = a.customFields?.nd_tweakwise_alternatesortorder;
                const orderB = b.customFields?.nd_tweakwise_alternatesortorder;
                if (orderA == null && orderB == null) return 0;
                if (orderA == null) return 1;
                if (orderB == null) return -1;
                return orderA - orderB;
            });
        }

        // When collapsed, preserve the raw API response order
        return [...items.value];
    });
    const filteredEntities = computed(() => {
        if (!brandSearch.value) return sortedEntities.value;
        return sortedEntities.value.filter(entity =>
            entity.translated?.name?.toLowerCase().includes(brandSearch.value.toLowerCase()),
        );
    });
    const emit = defineEmits(['invokeFilter']);

    const isOptionSelected = (id: string) => {
        return selectedOptions.value?.includes(id) ?? false;
    };

    const invokeFilter = (id: string, event?: Event) => {
        emit('invokeFilter', id, props.index);
    };
</script>
